import { Loader2Icon } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  maintenanceCustomerProjectsPageFromSearch,
  maintenanceCustomerProjectsPath,
} from '@/products/maintenance/features/maintenance-customer-projects-path';
import { MaintenanceDetailField } from '@/products/maintenance/features/maintenance-detail-field';
import { MaintenanceListFooter } from '@/products/maintenance/features/maintenance-list-footer';
import { MaintenanceRecordList } from '@/products/maintenance/features/maintenance-record-list';
import { MaintenanceStatusBadge } from '@/products/maintenance/features/maintenance-status-badge';
import {
  useMaintenanceCustomer,
  useMaintenanceProjects,
} from '@/products/maintenance/features/use-maintenance';
import {
  MAINTENANCE_CUSTOMERS_PATH,
  maintenanceCustomerEditPath,
  maintenanceCustomerProjectNewPath,
  maintenanceProjectPath,
} from '@/products/maintenance/maintenance-paths';

export function MaintenanceCustomerDetailPage() {
  const { customerId = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const projectPage = maintenanceCustomerProjectsPageFromSearch(search.toString());
  const customer = useMaintenanceCustomer(customerId);
  const projects = useMaintenanceProjects({
    page: projectPage,
    pageSize: 25,
    sortBy: 'name',
    sortOrder: 'asc',
    customerId,
  });

  usePageTitle(customer.data?.name ?? 'Kunde');

  if (customer.isPending) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Kunde wird geladen …
      </p>
    );
  }
  if (customer.isError || !customer.data) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Der Kunde wurde nicht gefunden.
      </p>
    );
  }

  const row = customer.data;
  const addressLines = [
    row.address.line1,
    row.address.line2,
    `${row.address.postalCode} ${row.address.city}`,
    row.address.country,
  ]
    .filter((line) => line && String(line).trim().length > 0)
    .join('\n');
  const projectItems = projects.data?.items ?? [];

  return (
    <div className="space-y-6">
      <p className="text-sm">
        <Link className="text-primary hover:underline" to={MAINTENANCE_CUSTOMERS_PATH}>
          ← Zurück zur Kundenliste
        </Link>
      </p>

      <PlatformPageHeader
        title={row.name}
        description={row.address.city}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="min-h-11" asChild>
              <Link to={maintenanceCustomerEditPath(row.id)}>Bearbeiten</Link>
            </Button>
            <Button className="min-h-11" disabled={!row.isActive} asChild={row.isActive}>
              {row.isActive ? (
                <Link to={maintenanceCustomerProjectNewPath(row.id)}>Projekt anlegen</Link>
              ) : (
                <span>Projekt anlegen</span>
              )}
            </Button>
          </div>
        }
      />

      {!row.isActive ? (
        <p className="text-sm text-muted-foreground">
          Inaktive Kunden erhalten keine neuen Projekte.
        </p>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            Stammdaten
            <MaintenanceStatusBadge status={row.isActive} />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <MaintenanceDetailField label="Kundennummer" value={row.customerNumber ?? '—'} />
          <MaintenanceDetailField label="Name" value={row.name} />
          <div className="min-w-0 space-y-1 sm:col-span-2">
            <p className="text-xs text-muted-foreground">Adresse</p>
            <p className="text-sm font-medium break-words whitespace-pre-line">{addressLines}</p>
          </div>
          <MaintenanceDetailField label="Ansprechpartner" value={row.contactName ?? '—'} />
          <MaintenanceDetailField label="E-Mail" value={row.contactEmail ?? '—'} />
          <MaintenanceDetailField label="Telefon" value={row.contactPhone ?? '—'} />
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Projekte</h2>
        {projects.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Projekte werden geladen …
          </p>
        ) : null}
        {projects.data?.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Projekte für diesen Kunden.</p>
        ) : null}
        {projectItems.length > 0 && projects.data ? (
          <>
            <MaintenanceRecordList
              rows={projectItems}
              getRowId={(project) => project.id}
              getHref={(project) => maintenanceProjectPath(project.id)}
              getTitle={(project) => project.name}
              getMeta={(project) => project.projectNumber ?? null}
              getStatus={(project) => project.isActive}
              columns={[
                {
                  id: 'number',
                  header: 'Nummer',
                  cell: (project) => project.projectNumber ?? '—',
                },
                {
                  id: 'installations',
                  header: 'Anlagen',
                  cell: (project) => String(project.installationCount ?? 0),
                },
              ]}
            />
            <MaintenanceListFooter
              page={projects.data.page}
              pageSize={projects.data.pageSize}
              total={projects.data.total}
              onPageChange={(page) =>
                setSearch(maintenanceCustomerProjectsPath(customerId, { page }))
              }
            />
          </>
        ) : null}
      </section>
    </div>
  );
}
